<template>
  <b-breadcrumb v-if="showBreadcrumbs" class="m-0">
    <template v-for="({ label, path }, index) in items">
      <b-breadcrumb-item
        :key="'breadcrumb' + path"
        :active="items.length - 1 === index"
        @click="$typedStore.activeVisualisation.updateStoryPath(path)"
      >
        <font-awesome-icon
          v-if="index === 0 && items.length > 1"
          class="back-button"
          icon="chevron-left"
        />
        <div class="label" :title="label">{{ label }}</div>
      </b-breadcrumb-item>
    </template>
  </b-breadcrumb>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { Tab, Path } from '@/types'

@Component
export default class ClickableBreadcrumbs extends Vue {
  get showBreadcrumbs() {
    const vis = this.$typedStore.activeVisualisation
    return vis.isBubbles || vis.isNestedBubbles || vis.isSunburst || vis.isXYBubbles
  }

  get storyPath() {
    return this.$typedStore.activeVisualisation.storyPath
  }

  get rootItem() {
    const activeTabs = this.$typedStore.activeVisualisation.visibleTabs
    const rootTab = activeTabs.find(({ path }: Tab) => new Path({ path }).isRoot)
    const subrootTab = activeTabs.find(({ path }: Tab) => new Path({ path }).isSubroot)
    return (this.storyPath.isSubroot && subrootTab) || rootTab || subrootTab
  }

  get items() {
    return [
      ...(this.rootItem ? [this.rootItem] : []).map((tab) => ({
        path: tab.path,
        label: tab.label ?? tab.path,
      })),
      ...this.storyPath.partPaths
        .filter((path) => !path.isRootOrSubroot)
        .map((path) => ({
          path: path.toString(),
          label:
            path.level === this.$typedStore.activeVisualisation.leafLevel
              ? this.projectLabel
              : this.getLabel(path),
        })),
    ]
  }

  get projectLabelField() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig
      .projectLabelField
  }

  get projectLabel() {
    const treeItem = this.$typedStore.visualisationData.treeRoot.search(this.storyPath)
    return (treeItem && treeItem?.data && treeItem.data[this.projectLabelField]) || ''
  }

  get rootLabel() {
    return this.getLabel(Path.root)
  }

  getLabel(path: Path) {
    return (
      this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.label(path) ||
      path.name
    )
  }
}
</script>

<style lang="scss">
.breadcrumb {
  --breadcrumb-height: 30px;
  @media (max-width: 932px) {
    position: absolute;
    z-index: 3;
  }
  background-color: transparent;
  font-weight: 400;
  padding: 5px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  @media (max-width: 1024px) {
    max-width: 100%;
  }

  .breadcrumb-item {
    background-color: #eee;
    transition: background-color 0.2s;
    height: var(--breadcrumb-height);
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: min(120px, 31vw);
    font-size: 12px;
    padding: 0 1em;
    margin-right: 1px;
    position: relative;
    &:hover {
      cursor: pointer;
      background-color: #ccc;
      a {
        text-decoration: none;
      }
    }
    span,
    a {
      width: 100%;
      align-items: center;
      .back-button {
        font-size: 16px;
      }
      .label {
        flex: 1;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-left: auto;
      }
    }
    &:first-child {
      padding: 0 1em 0 0.5em;
      border-radius: calc(var(--breadcrumb-height) / 2) 0 0 calc(var(--breadcrumb-height) / 2);
      a {
        display: flex;
        .label {
          margin-left: 0.5em;
        }
      }
    }
    &:last-child {
      padding: 0 1em 0 0.5em;
      border-radius: 0 calc(var(--breadcrumb-height) / 2) calc(var(--breadcrumb-height) / 2) 0;
    }
    &:first-child:last-child {
      padding: 0 1em;
      border-radius: calc(var(--breadcrumb-height) / 2);
    }
    a {
      color: black;
    }
    &.active {
      color: black;
    }
    + .breadcrumb-item {
      &::before {
        content: '';
      }
    }
  }
  @media (min-width: 1024px) {
    .breadcrumb-item {
      max-width: 160px;
    }
  }
}
</style>
